import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerModal from '../components/CustomerModal';
import CustomerMatchBanner from '../components/CustomerMatchBanner';
import { useImageAttach } from '../components/MessageChat';
import { useSocket, useUnreadCountContext } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const MESSAGES_PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const CUSTOMER_SEARCH_DEBOUNCE_MS = 200;

export default function Messages() {
  const navigate = useNavigate();
  const messagesContainerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);
  const customerAutocompleteRef = useRef(null);
  const selectedThreadRef = useRef(null);
  const filterTypeRef = useRef('all');
  const socket = useSocket();
  const { fetchUnreadCount } = useUnreadCountContext();

  const [contentHeight, setContentHeight] = useState(0);
  const [threads, setThreads] = useState([]);
  const [filteredThreads, setFilteredThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [scrollPositionTracked, setScrollPositionTracked] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'unread', or 'archived'

  // Keep refs in sync with state for use inside socket handler
  useEffect(() => {
    selectedThreadRef.current = selectedThread;
  }, [selectedThread]);

  useEffect(() => {
    filterTypeRef.current = filterType;
  }, [filterType]);

  // Set up Socket.io listener for real-time message updates in the Messages page
  // Uses the global socket from App.js context (shared connection)
  useEffect(() => {
    if (!socket) return;

    socket.on('new-message', async (data) => {
      console.log('Messages page: New message received via Socket.io:', data);
      // Refresh thread list using current filter
      fetchThreads(filterTypeRef.current);
      // If the new message is in the currently selected conversation, refresh messages
      const currentThread = selectedThreadRef.current;
      if (currentThread && currentThread.conversation_sid === data.conversationSid) {
        // If there's a suggested customer, update the thread with it
        if (data.suggestedCustomer) {
          setThreads(prevThreads =>
            prevThreads.map(t =>
              t.conversation_sid === data.conversationSid
                ? { ...t, suggested_customer: data.suggestedCustomer, suggested_customer_id: data.suggestedCustomer.id }
                : t
            )
          );
          setSelectedThread(prev =>
            prev && prev.conversation_sid === data.conversationSid
              ? { ...prev, suggested_customer: data.suggestedCustomer, suggested_customer_id: data.suggestedCustomer.id }
              : prev
          );
        }

        // Build a minimal message object from socket data and append to displayed messages
        const newMessage = {
          sid: `socket-${Date.now()}`,
          index: displayedMessages.length > 0 ? (displayedMessages[displayedMessages.length - 1].index || 0) + 1 : 0,
          author: data.customerPhone ? 'Customer' : 'Store',
          body: data.body || '(Image)',
          dateCreated: new Date(data.timestamp).toISOString(),
        };
        setDisplayedMessages(prev => [...prev, newMessage]);
        // Scroll to bottom to show new message
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 100);
      }
      // Note: unread count is updated globally by App.js socket listener
    });

    return () => {
      socket.off('new-message');
    };
  }, [socket]);
  
  // Customer autocomplete state
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isCustomerSearching, setIsCustomerSearching] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]); // Array of { id, name, phone }
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneNumberDisabled, setIsPhoneNumberDisabled] = useState(false);
  
  // Create customer modal state
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const [pendingCustomerPhone, setPendingCustomerPhone] = useState('');
  
  // Form state for creating messages
  const [newMessage, setNewMessage] = useState({
    customer_ids: [],
    phone_number: '',
    message_text: ''
  });

  // Image attachments — separate instances for the reply form and the create modal
  const replyAttach = useImageAttach();
  const createAttach = useImageAttach();
  const [creating, setCreating] = useState(false);

  // Clear reply attachments when switching threads or opening the create modal
  useEffect(() => {
    replyAttach.clearAttachments();
  }, [selectedThread?.conversation_sid]);
  useEffect(() => {
    if (showCreateForm) createAttach.clearAttachments();
  }, [showCreateForm]);

  // Handle scroll to detect when user scrolls to top
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop < 50) {
      setScrollPositionTracked(true);
    }
  };

  const fetchThreads = async (filter) => {
    try {
      const currentFilter = filter !== undefined ? filter : filterType;
      const showArchived = currentFilter === 'archived';
      const res = await fetch(`${API_URL}/conversations?archived=${showArchived}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setThreads(data.threads);
        if (currentFilter === 'unread') {
          setFilteredThreads(data.threads.filter(t => !t.is_read));
        } else if (currentFilter === 'archived') {
          setFilteredThreads(data.threads.filter(t => t.archived));
        } else {
          setFilteredThreads(data.threads);
        }
      }
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchThreads = useCallback(async (query, filter) => {
    const showArchived = filter === 'archived';
    if (!query || query.trim() === '') {
      if (filter === 'unread') {
        setFilteredThreads(threads.filter(t => !t.is_read));
      } else if (filter === 'archived') {
        setFilteredThreads(threads.filter(t => t.archived));
      } else {
        setFilteredThreads(threads);
      }
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const res = await fetch(`${API_URL}/conversations/search?q=${encodeURIComponent(query)}&unread=${filter === 'unread'}&archived=${showArchived}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setFilteredThreads(data.threads);
      }
    } catch (error) {
      console.error('Failed to search threads:', error);
    } finally {
      setIsSearching(false);
    }
  }, [threads]);

  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchQuery('');
    setSelectedThread(null);
    fetchThreads(newFilter);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchThreads(query, filterType);
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => {
    if (searchQuery && searchQuery.trim()) {
      searchThreads(searchQuery, filterType);
    }
  }, [filterType]);

  useEffect(() => {
    fetchThreads();
    
    const calculateHeight = () => {
      const navHeight = 64;
      const paddingY = 48;
      const availableHeight = window.innerHeight - navHeight - paddingY;
      setContentHeight(availableHeight);
    };
    
    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => {
      window.removeEventListener('resize', calculateHeight);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (searchQuery && searchQuery.trim()) {
      searchThreads(searchQuery, filterType);
    } else {
      if (filterType === 'unread') {
        setFilteredThreads(threads.filter(t => !t.is_read));
      } else {
        setFilteredThreads(threads);
      }
    }
  }, [filterType, threads, searchQuery, searchThreads]);

  const fetchMessages = async (conversationSid, beforeIndex) => {
    try {
      let url = `${API_URL}/conversations/messages/${conversationSid}?limit=${MESSAGES_PER_PAGE}`;
      if (beforeIndex !== undefined && beforeIndex !== null) {
        url += `&beforeIndex=${beforeIndex}`;
      }
      const res = await fetch(url, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        return data.messages; // Backend returns oldest-first (chronological)
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      return [];
    }
  };

  const loadInitialMessages = async (conversationSid) => {
    const fetchedMessages = await fetchMessages(conversationSid);
    // Backend returns messages oldest-first (chronological order)
    setDisplayedMessages(fetchedMessages);
    setHasMore(fetchedMessages.length === MESSAGES_PER_PAGE);
    setScrollPositionTracked(false);
    
    // Scroll to bottom to show newest messages
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const loadMoreMessages = async () => {
    if (!selectedThread || loadingMore || !hasMore || !scrollPositionTracked || displayedMessages.length === 0) return;
    
    const container = messagesContainerRef.current;
    const scrollHeightBefore = container.scrollHeight;
    
    setLoadingMore(true);
    
    // Get the index of the oldest currently displayed message for pagination
    const oldestMessage = displayedMessages[0];
    const beforeIndex = oldestMessage?.index;
    
    // Fetch older messages using beforeIndex cursor
    const olderMessages = await fetchMessages(selectedThread.conversation_sid, beforeIndex);
    
    if (olderMessages.length > 0) {
      setDisplayedMessages(prev => [...olderMessages, ...prev]);
      setHasMore(olderMessages.length === MESSAGES_PER_PAGE);
      
      // Maintain scroll position after prepending older messages
      requestAnimationFrame(() => {
        const scrollHeightDiff = container.scrollHeight - scrollHeightBefore;
        container.scrollTop = scrollHeightDiff;
      });
    } else {
      setHasMore(false);
    }
    setLoadingMore(false);
  };

  const handleThreadClick = async (thread) => {
    setSelectedThread(thread);
    
    // Mark as read
    try {
      await fetch(`${API_URL}/conversations/${thread.conversation_sid}/mark-read`, {
        method: 'PATCH',
        credentials: 'include'
      });
      
      setThreads(prevThreads =>
        prevThreads.map(t =>
          t.conversation_sid === thread.conversation_sid
            ? { ...t, is_read: true }
            : t
        )
      );
      
      await fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
    
    await loadInitialMessages(thread.conversation_sid);
  };

  const handleArchiveThread = async () => {
    if (!selectedThread) return;
    if (!confirm('Archive this conversation?')) return;
    
    try {
      await fetch(`${API_URL}/conversations/${selectedThread.conversation_sid}/archive`, {
        method: 'PATCH',
        credentials: 'include'
      });
      
      await fetchThreads();
      setSelectedThread(null);
    } catch (error) {
      console.error('Failed to archive thread:', error);
    }
  };

  const handleDeleteThread = async () => {
    if (!selectedThread) return;
    if (!confirm('Permanently delete this conversation? This action cannot be undone.')) return;
    
    try {
      await fetch(`${API_URL}/conversations/${selectedThread.conversation_sid}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      setThreads(prevThreads => prevThreads.filter(t => t.conversation_sid !== selectedThread.conversation_sid));
      setFilteredThreads(prevFiltered => prevFiltered.filter(t => t.conversation_sid !== selectedThread.conversation_sid));
      setSelectedThread(null);
    } catch (error) {
      console.error('Failed to delete thread:', error);
    }
  };

  const handleCreateMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.phone_number || (!newMessage.message_text.trim() && createAttach.attachments.length === 0)) {
      alert('Phone number and message text are required');
      return;
    }
    if (creating || createAttach.uploading) return;

    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/conversations/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          phoneNumber: newMessage.phone_number,
          customerName: selectedCustomers.length > 0
            ? selectedCustomers.map(c => c.name).join(', ')
            : undefined,
          customer_id: newMessage.customer_ids.length > 0 ? newMessage.customer_ids[0] : undefined
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create conversation');
      }

      const data = await res.json();
      
      // If conversation was created, upload images first, then send the initial message
      if (data.conversationSid) {
        const mediaUrls = createAttach.attachments.length > 0
          ? await createAttach.uploadAll()
          : [];
        const res2 = await fetch(`${API_URL}/conversations/reply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            conversationSid: data.conversationSid,
            message: newMessage.message_text.trim(),
            mediaUrls
          })
        });
        if (!res2.ok) {
          const errData = await res2.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to send message');
        }
      }

      setShowCreateForm(false);
      setNewMessage({ customer_ids: [], phone_number: '', message_text: '' });
      createAttach.clearAttachments();
      setSelectedCustomers([]);
      setPhoneNumber('');
      setIsPhoneNumberDisabled(false);
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
      await fetchThreads();
    } catch (error) {
      console.error('Failed to create message:', error);
      alert(error.message || 'Failed to create message');
    } finally {
      setCreating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewMessage(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!selectedThread) return;
    if (!replyText.trim() && replyAttach.attachments.length === 0) return;
    if (replyAttach.uploading) return;

    try {
      // Upload any attached images first, then send with their URLs
      const mediaUrls = replyAttach.attachments.length > 0
        ? await replyAttach.uploadAll()
        : [];

      const res = await fetch(`${API_URL}/conversations/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conversationSid: selectedThread.conversation_sid,
          message: replyText.trim(),
          mediaUrls
        })
      });

      if (res.ok) {
        setReplyText('');
        replyAttach.clearAttachments();
        await fetchThreads();
        // Refresh unread count badge since we just marked this conversation as read
        await fetchUnreadCount();
        // Refresh messages to show the new reply
        await loadInitialMessages(selectedThread.conversation_sid);
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Failed to send reply:', error);
      alert(error.message || 'Failed to send reply');
    }
  };

  const removeCreateAttachment = (i) => createAttach.removeAttachment(i);

  const formatDateTime = (datetime, showYear = false) => {
    const date = new Date(datetime);
    const currentYear = new Date().getFullYear();
    const messageYear = date.getFullYear();
    const shouldShowYear = showYear || messageYear !== currentYear;
    
    const options = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    };
    
    if (shouldShowYear) {
      options.year = 'numeric';
    }
    
    return date.toLocaleString('en-US', options);
  };

  // Customer autocomplete functions
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.trim() === '') {
      setCustomerSearchResults([]);
      setShowCustomerDropdown(false);
      setIsCustomerSearching(false);
      return;
    }

    setIsCustomerSearching(true);
    try {
      const res = await fetch(`${API_URL}/customers/search/autocomplete?q=${encodeURIComponent(query)}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCustomerSearchResults(data.customers);
        setShowCustomerDropdown(true);
      }
    } catch (error) {
      console.error('Failed to search customers:', error);
    } finally {
      setIsCustomerSearching(false);
    }
  }, []);

  const handleCustomerSearchChange = (e) => {
    const query = e.target.value;
    setCustomerSearchQuery(query);

    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }

    customerSearchTimeoutRef.current = setTimeout(() => {
      searchCustomers(query);
    }, CUSTOMER_SEARCH_DEBOUNCE_MS);
  };

  const handleCustomerSelect = (customer) => {
    if (selectedCustomers.some(c => c.id === customer.id)) {
      return;
    }

    const newSelectedCustomer = {
      id: customer.id,
      name: `${customer.first_name} ${customer.last_name}`.trim(),
      phone: customer.mobile_phone || customer.home_phone || customer.work_phone || ''
    };

    const newSelectedCustomers = [...selectedCustomers, newSelectedCustomer];
    setSelectedCustomers(newSelectedCustomers);
    
    if (newSelectedCustomers.length === 1) {
      setPhoneNumber(newSelectedCustomer.phone);
      setIsPhoneNumberDisabled(true);
      setNewMessage(prev => ({ ...prev, phone_number: newSelectedCustomer.phone }));
    }
    
    setNewMessage(prev => ({
      ...prev,
      customer_ids: newSelectedCustomers.map(c => c.id)
    }));

    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
  };

  const handleRemoveCustomerTag = (customerId) => {
    const newSelectedCustomers = selectedCustomers.filter(c => c.id !== customerId);
    setSelectedCustomers(newSelectedCustomers);
    
    setNewMessage(prev => ({
      ...prev,
      customer_ids: newSelectedCustomers.map(c => c.id)
    }));

    if (newSelectedCustomers.length === 0) {
      setPhoneNumber('');
      setIsPhoneNumberDisabled(false);
      setNewMessage(prev => ({ ...prev, phone_number: '' }));
    } else {
      const firstCustomer = newSelectedCustomers[0];
      setPhoneNumber(firstCustomer.phone);
      setNewMessage(prev => ({ ...prev, phone_number: firstCustomer.phone }));
    }
  };

  const handleOpenCreateCustomerModal = () => {
    setPendingCustomerPhone(customerSearchQuery);
    setShowCreateCustomerModal(true);
    setShowCustomerDropdown(false);
    setCustomerSearchQuery('');
  };

  const handleCreateCustomerModalClose = () => {
    setShowCreateCustomerModal(false);
    setPendingCustomerPhone('');
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
  };

  const handleCreateCustomerModalSuccess = async (newCustomer) => {
    setShowCreateCustomerModal(false);
    
    const newSelectedCustomer = {
      id: newCustomer.id,
      name: `${newCustomer.first_name} ${newCustomer.last_name}`.trim(),
      phone: newCustomer.mobile_phone || newCustomer.home_phone || newCustomer.work_phone || ''
    };
    
    const newSelectedCustomers = [...selectedCustomers, newSelectedCustomer];
    setSelectedCustomers(newSelectedCustomers);
    
    setPhoneNumber(newSelectedCustomer.phone);
    setIsPhoneNumberDisabled(true);
    setNewMessage(prev => ({
      ...prev,
      phone_number: newSelectedCustomer.phone,
      customer_ids: newSelectedCustomers.map(c => c.id)
    }));
    
    // If we're viewing a thread, link the customer
    if (selectedThread && selectedThread.phone_number === newSelectedCustomer.phone) {
      try {
        await fetch(`${API_URL}/conversations/${selectedThread.conversation_sid}/link-customer`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            customer_id: newCustomer.id,
            customer_name: newSelectedCustomer.name
          })
        });
        
        setSelectedThread(prev => ({
          ...prev,
          customer_id: newCustomer.id,
          customer_name: newSelectedCustomer.name
        }));
        
        setThreads(prevThreads =>
          prevThreads.map(t =>
            t.conversation_sid === selectedThread.conversation_sid
              ? { ...t, customer_id: newCustomer.id, customer_name: newSelectedCustomer.name }
              : t
          )
        );
      } catch (error) {
        console.error('Failed to link customer:', error);
      }
    }
    
    setPendingCustomerPhone('');
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerAutocompleteRef.current && !customerAutocompleteRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (customerSearchTimeoutRef.current) {
        clearTimeout(customerSearchTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow flex flex-col mx-auto" style={{ height: contentHeight > 0 ? `${contentHeight}px` : 'auto', minHeight: '500px' }}>
      <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
        <h1 className="text-2xl font-bold text-gray-800">Message Center</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700"
        >
          {showCreateForm ? 'Cancel' : 'Create Message'}
        </button>
      </div>

      {/* Create Message Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800">Create New Message</h2>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              <form onSubmit={handleCreateMessage}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Customer Autocomplete Field */}
                  <div className="md:col-span-2" ref={customerAutocompleteRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customers (optional)
                    </label>
                    <div className="relative">
                      <div className={`w-full min-h-[42px] px-3 py-2 border rounded-md shadow-sm flex flex-wrap items-center gap-1 ${
                        selectedCustomers.length > 0
                          ? 'border-gray-300 bg-gray-100'
                          : 'border-gray-300 bg-white focus-within:outline-none focus-within:ring-blue-500 focus-within:border-blue-500'
                      }`}>
                        {selectedCustomers.map((customer) => (
                          <span
                            key={customer.id}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {customer.name}
                            <button
                              type="button"
                              onClick={() => handleRemoveCustomerTag(customer.id)}
                              className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                            >
                              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          autoComplete="off"
                          value={customerSearchQuery}
                          onChange={handleCustomerSearchChange}
                          onFocus={() => customerSearchQuery && setShowCustomerDropdown(true)}
                          disabled={selectedCustomers.length > 0}
                          className={`min-w-[120px] outline-none text-sm ${
                            selectedCustomers.length > 0
                              ? 'flex-1 bg-transparent text-gray-400 cursor-not-allowed'
                              : 'flex-1'
                          }`}
                          placeholder={selectedCustomers.length === 0 ? "Search customers..." : "Customer selected"}
                        />
                        {isCustomerSearching && (
                          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        )}
                      </div>
                      
                      {showCustomerDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                          <div
                            onClick={handleOpenCreateCustomerModal}
                            className="px-3 py-2 border-b border-gray-200 hover:bg-gray-50 cursor-pointer text-sm text-blue-600 font-medium flex items-center gap-2"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create new customer
                          </div>
                          {customerSearchResults.length === 0 ? (
                            <div className="p-3 text-sm text-gray-500 text-center">
                              No customers found
                            </div>
                          ) : (
                            <ul className="py-1">
                              {customerSearchResults.map((customer) => (
                                <li
                                  key={customer.id}
                                  onClick={() => handleCustomerSelect(customer)}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex flex-col">
                                      <span className="font-medium text-gray-900">
                                        {customer.first_name} {customer.last_name}
                                      </span>
                                      {(customer.city || customer.state) && (
                                        <span className="text-xs text-gray-400 mt-0.5">
                                          {[customer.city, customer.state].filter(Boolean).join(', ')}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex flex-col text-right gap-0.5">
                                      {customer.mobile_phone && (
                                        <span className="text-xs text-gray-500">Mobile: {customer.mobile_phone}</span>
                                      )}
                                      {customer.home_phone && (
                                        <span className="text-xs text-gray-500">Home: {customer.home_phone}</span>
                                      )}
                                      {customer.work_phone && (
                                        <span className="text-xs text-gray-500">Work: {customer.work_phone}</span>
                                      )}
                                      {!customer.mobile_phone && !customer.home_phone && !customer.work_phone && (
                                        <span className="text-xs text-gray-500">{customer.email || 'No contact'}</span>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Phone Number Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="text"
                      autoComplete="off"
                      name="phone_number"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        setNewMessage(prev => ({ ...prev, phone_number: e.target.value }));
                      }}
                      disabled={isPhoneNumberDisabled}
                      required
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                        isPhoneNumberDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      placeholder="Enter phone number"
                    />
                  </div>
                  
                  {/* Message Text */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message Text
                    </label>
                    <textarea
                      name="message_text"
                      value={newMessage.message_text}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter message text (optional if attaching images)"
                    />
                  </div>

                  {/* Image Attachments */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Images (optional)
                    </label>
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => createAttach.fileInputRef.current && createAttach.fileInputRef.current.click()}
                        disabled={createAttach.attachments.length >= 5}
                        title="Attach image(s) — max 5, 10MB each"
                        className="flex-shrink-0 bg-gray-100 text-gray-600 border border-gray-300 rounded-md px-3 py-2 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        + Add images
                      </button>
                      <input
                        ref={createAttach.fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        multiple
                        className="hidden"
                        onChange={createAttach.handleFileChange}
                      />
                    </div>
                    {createAttach.attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {createAttach.attachments.map((att, i) => (
                          <div key={i} className="relative w-16 h-16">
                            <img
                              src={att.previewUrl}
                              alt={`Attachment ${i + 1}`}
                              className="w-16 h-16 object-cover rounded-md border border-gray-300"
                            />
                            <button
                              type="button"
                              onClick={() => removeCreateAttachment(i)}
                              title="Remove image"
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs leading-none flex items-center justify-center hover:bg-red-600"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating || createAttach.uploading || (!newMessage.message_text.trim() && createAttach.attachments.length === 0)}
                    className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createAttach.uploading ? 'Uploading…' : creating ? 'Sending…' : 'Create Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Thread List */}
        <div className="w-1/3 border-r flex flex-col">
          <div className="p-4 bg-gray-50 border-b flex-shrink-0">
            <h3 className="font-semibold text-gray-700 mb-2">Conversations</h3>
            <div className="flex space-x-2 mb-2 ml-2">
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  filterType === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange('unread')}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  filterType === 'unread'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Unread
              </button>
              <button
                onClick={() => handleFilterChange('archived')}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  filterType === 'archived'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Archived
              </button>
            </div>
            <div className="relative">
              <input
                type="text"
                autoComplete="off"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search conversations..."
                className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
              )}
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredThreads.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No conversations found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery
                    ? `No conversations match "${searchQuery}"`
                    : filterType === 'unread'
                      ? 'You have no unread messages'
                      : filterType === 'archived'
                        ? 'No archived conversations'
                        : 'No conversations yet'}
                </p>
              </div>
            ) : (
              filteredThreads.map((thread) => (
                <div
                  key={thread.conversation_sid}
                  onClick={() => handleThreadClick(thread)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                    selectedThread?.conversation_sid === thread.conversation_sid ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      {thread.customer_name && thread.customer_name.trim() ? (
                        <>
                          {thread.customer_id ? (
                            <span
                              className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customers/${thread.customer_id}/dashboard`);
                              }}
                            >
                              {thread.customer_name}
                            </span>
                          ) : (
                            <span className="font-medium text-gray-800">{thread.customer_name}</span>
                          )}
                          <span className="text-xs text-gray-500 ml-2">({thread.phone_number})</span>
                        </>
                      ) : (
                        <span className="font-medium text-gray-800">{thread.phone_number}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {!thread.is_read && (
                        <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">New</span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 truncate mt-1">
                    {thread.last_message}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {thread.last_message_at ? formatDateTime(thread.last_message_at) : 'No messages yet'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Details */}
        <div className="w-2/3 flex flex-col">
          {selectedThread ? (
            <>
              <div className="p-4 bg-gray-50 border-b flex-shrink-0">
                {/* Customer Match Banner */}
                {selectedThread.suggested_customer && !selectedThread.customer_id && (
                  <CustomerMatchBanner
                    conversationSid={selectedThread.conversation_sid}
                    suggestedCustomer={selectedThread.suggested_customer}
                    onLinked={(customer) => {
                      setSelectedThread(prev => ({
                        ...prev,
                        customer_id: customer.id,
                        customer_name: customer.name,
                        suggested_customer: null,
                        suggested_customer_id: null
                      }));
                      setThreads(prevThreads =>
                        prevThreads.map(t =>
                          t.conversation_sid === selectedThread.conversation_sid
                            ? { ...t, customer_id: customer.id, customer_name: customer.name, suggested_customer: null, suggested_customer_id: null }
                            : t
                        )
                      );
                      // Refresh to ensure DB is in sync
                      fetchThreads(filterType);
                    }}
                    onDismissed={() => {
                      setSelectedThread(prev => ({
                        ...prev,
                        suggested_customer: null,
                        suggested_customer_id: null
                      }));
                      setThreads(prevThreads =>
                        prevThreads.map(t =>
                          t.conversation_sid === selectedThread.conversation_sid
                            ? { ...t, suggested_customer: null, suggested_customer_id: null }
                            : t
                        )
                      );
                    }}
                  />
                )}
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700 flex items-center gap-1">
                    <span>Chat with</span>
                    {selectedThread.customer_id ? (
                      <span
                        onClick={() => navigate(`/customers/${selectedThread.customer_id}/dashboard`)}
                        className="cursor-pointer text-blue-600 hover:underline hover:text-blue-800"
                      >
                        {selectedThread.customer_name || 'unknown'}
                      </span>
                    ) : (
                      <span>{selectedThread.customer_name || 'unknown'}</span>
                    )}
                    <span>({selectedThread.phone_number})</span>
                  </h3>
                  <div className="flex items-center gap-2">
                    {(!selectedThread.customer_id || !selectedThread.customer_name || !selectedThread.customer_name.trim()) && !selectedThread.suggested_customer && (
                      <button
                        onClick={() => {
                          setPendingCustomerPhone(selectedThread.phone_number);
                          setShowCreateCustomerModal(true);
                        }}
                        className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200"
                        title="Create customer for this phone number"
                      >
                        + Customer
                      </button>
                    )}
                    {selectedThread.archived ? (
                      <button
                        onClick={handleDeleteThread}
                        className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                        title="Delete conversation permanently"
                      >
                        Delete
                      </button>
                    ) : (
                      <button
                        onClick={handleArchiveThread}
                        className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded hover:bg-yellow-200"
                        title="Archive this conversation"
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                className="overflow-y-auto flex-1 bg-gray-100 p-4"
              >
                {hasMore && scrollPositionTracked && (
                  <div className="flex justify-center mb-4">
                    <button
                      onClick={loadMoreMessages}
                      disabled={loadingMore}
                      className="bg-white text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMore ? 'Loading...' : 'Load older messages'}
                    </button>
                  </div>
                )}
                
                {displayedMessages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    No messages yet
                  </div>
                ) : (
                  displayedMessages.map((message, index) => (
                    <div
                      key={message.sid || index}
                      className={`flex mb-3 ${
                        message.author === 'Store' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-xs md:max-w-md lg:max-w-lg rounded-2xl overflow-hidden ${
                          message.author === 'Store' ? 'rounded-tr-sm' : ''
                        }`}
                      >
                        {/* Sender name header bar */}
                        {(message.senderUser || message.author !== 'Store') && (
                          <div
                            className={`px-3 py-1 text-xs font-medium border-b ${
                              message.author === 'Store'
                                ? 'bg-blue-600 text-blue-100 border-blue-700'
                                : 'bg-gray-100 text-gray-500 border-gray-200'
                            }`}
                          >
                            {message.senderUser
                              ? message.senderUser.userName
                              : message.author === 'Store'
                                ? 'Store'
                                : 'Customer'}
                          </div>
                        )}
                        <div
                          className={`px-4 py-2 ${
                            message.author === 'Store'
                              ? 'bg-blue-500 text-white'
                              : 'bg-white text-gray-800'
                          }`}
                        >
                          {message.body && (
                            <div className="text-sm break-words">
                              {message.body}
                            </div>
                          )}
                          {(() => {
                            if (message.numMedia && parseInt(message.numMedia) > 0 && message.mediaUrl) {
                              console.log('Rendering media for message', message.sid, message.mediaUrl);
                              return (
                                <div className="mt-2">
                                  {message.mediaUrl.map((media, i) => (
                                    <img
                                      key={i}
                                      src={media}
                                      alt={`Attachment ${i + 1}`}
                                      className="max-w-full rounded-lg cursor-pointer"
                                      onClick={() => window.open(media, '_blank')}
                                      onError={(e) => console.error('Failed to load image', media, e)}
                                    />
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          })()}
                          <div className={`flex items-center justify-end mt-1 text-xs ${
                            message.author === 'Store' ? 'text-blue-100' : 'text-gray-400'
                          }`}>
                            <span>{formatDateTime(message.dateCreated, true)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Reply Form */}
              <div className="p-4 bg-gray-50 border-t flex-shrink-0">
                <form onSubmit={handleSendReply} autoComplete="off">
                  {/* Attached image previews */}
                  {replyAttach.attachments.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {replyAttach.attachments.map((att, i) => (
                        <div key={i} className="relative w-16 h-16">
                          <img
                            src={att.previewUrl}
                            alt={`Attachment ${i + 1}`}
                            className="w-16 h-16 object-cover rounded-md border border-gray-300"
                          />
                          <button
                            type="button"
                            onClick={() => replyAttach.removeAttachment(i)}
                            title="Remove image"
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs leading-none flex items-center justify-center hover:bg-red-600"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <button
                      type="button"
                      onClick={() => replyAttach.fileInputRef.current && replyAttach.fileInputRef.current.click()}
                      disabled={replyAttach.attachments.length >= 5}
                      title="Attach image(s) — max 5, 10MB each"
                      className="flex-shrink-0 bg-white text-gray-600 border border-gray-300 rounded-md px-3 py-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                    <input
                      ref={replyAttach.fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      multiple
                      className="hidden"
                      onChange={replyAttach.handleFileChange}
                    />
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply here..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
                    />
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={!replyText.trim() && replyAttach.attachments.length === 0 || replyAttach.uploading}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {replyAttach.uploading ? 'Uploading…' : 'Send Reply'}
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No conversation selected</h3>
                <p className="mt-1 text-sm text-gray-500">Select a conversation from the list to view and reply to messages.</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Create Customer Modal */}
      <CustomerModal
        showModal={showCreateCustomerModal}
        onClose={handleCreateCustomerModalClose}
        onSuccess={handleCreateCustomerModalSuccess}
        initialData={pendingCustomerPhone ? { mobile_phone: pendingCustomerPhone } : {}}
      />
    </div>
  );
}
